/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";
import { isFunction, isNonEmptyArray } from "@adapptio/lib-shared";
import { ButtonBase } from "../ButtonBase";
import { IPropColor } from "../../CommonProps";
import { Tooltip } from "../Tooltip";
import { useForkRef } from "../../../hooks";
import { isSelectOptionItem, TSelectOption } from "../Select";
import { MenuItem } from "../MenuItem";
import { ListItemIcon } from "../ListItemIcon";
import { Icon } from "../Icon";
import { ListItemText } from "../ListItemText";
import { Separator } from "../Separator";
import { Menu } from "../Menu";

import "./Button.scss";

/**
 * Button props
 */
export interface IButtonProps<Value extends string | number | null = string | number | null>
	extends Omit<React.ComponentPropsWithoutRef<"button">, keyof IPropColor | "onChange" | "onClick">,
		IPropColor {
	selected?: boolean;
	value?: Value;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>, value: Value) => void;
	onChange?: (event: React.MouseEvent<HTMLButtonElement>, value: Value) => void;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	tooltip?: string;
	options?: TSelectOption[];
}

/**
 * Generic Button component
 */
const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
	(
		{
			children,
			className,
			type,
			disabled,
			onClick,
			onChange,
			value,
			selected,
			color,
			startIcon,
			endIcon,
			tooltip,
			options,
			...restProps
		},
		ref
	) => {
		const [ menuOpen, toggleMenu, openMenu, closeMenu ] = useToggle(false);
		const dropdownTrigger = isNonEmptyArray(options);

		const baseClass = BEM("edt-button", null, {
			color,
			selected,
			disabled,
			icon: !!(startIcon || endIcon),
			label: !!children
		});

		const btnRef = useRef();
		const forkRef = useForkRef(ref, btnRef);

		const handleClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
			(event) => {
				if (disabled) {
					return;
				}

				if (isFunction(onClick)) {
					onClick(event, value);

					if (event.defaultPrevented) {
						return;
					}
				}

				if (isFunction(onChange)) {
					onChange(event, value);

					if (event.defaultPrevented) {
						return;
					}
				}

				if (dropdownTrigger) {
					toggleMenu();
				}
			},
			[ onClick, disabled, value, toggleMenu ]
		);

		if (className) {
			baseClass.add(className);
		}

		return (
			<ButtonBase
				{...restProps}
				className={baseClass.toClassName()}
				tabIndex={disabled ? -1 : 0}
				disabled={disabled}
				type={type}
				onClick={handleClick}
				Component="button"
				aria-pressed={selected}
				ref={forkRef}
			>
				{startIcon}
				{children}
				{endIcon}

				{tooltip ? (
					<Tooltip for={btnRef} openDelay={1200}>
						{tooltip}
					</Tooltip>
				) : null}

				{dropdownTrigger && (
					<Menu anchorRef={btnRef} open={menuOpen} onClose={closeMenu}>
						{options?.map((option, index) =>
							isSelectOptionItem(option) ? (
								<MenuItem key={option.id} value={option.value} onClick={option.onClick}>
									{option.icon && (
										<ListItemIcon
											className={baseClass.element("option-icon").toClassName()}
										>
											<Icon name={option.icon} />
										</ListItemIcon>
									)}
									{option.label && (
										<ListItemText align={option.icon ? "left" : "center"}>
											{option.label}
										</ListItemText>
									)}
								</MenuItem>
							) : (
								<Separator key={`separator-${index}`} />
							)
						)}
					</Menu>
				)}
			</ButtonBase>
		);
	}
);

Button.defaultProps = {
	type: "button"
};

const ButtonMemo = React.memo(Button);

export { ButtonMemo as Button };
